import React, { useState, useEffect } from "react";
import Card from "components/card";
import { MdClose, MdStar } from "react-icons/md";
import { FaHotel, FaWifi, FaParking, FaSwimmingPool } from "react-icons/fa";

const HotelResultsModal = ({ isOpen, onClose, onSelect, searchData }) => {
  const [loading, setLoading] = useState(true);
  const [hotels, setHotels] = useState([]);

  const mockHotels = [
    {
      id: 1,
      name: "Grand Plaza Hotel",
      type: "5-Star Luxury",
      location: searchData?.location || "Downtown",
      checkIn: searchData?.checkIn || "2024-01-15",
      checkOut: searchData?.checkOut || "2024-01-17",
      rating: "4.8",
      price: "$299/night",
      amenities: ["Free WiFi", "Pool", "Parking"]
    },
    {
      id: 2,
      name: "Business Center Inn", 
      type: "4-Star Business",
      location: searchData?.location || "Downtown",
      checkIn: searchData?.checkIn || "2024-01-15",
      checkOut: searchData?.checkOut || "2024-01-17",
      rating: "4.5",
      price: "$189/night",
      amenities: ["Free WiFi", "Business Center", "Gym"]
    },
    {
      id: 3,
      name: "Comfort Suites",
      type: "3-Star Comfort", 
      location: searchData?.location || "Downtown",
      checkIn: searchData?.checkIn || "2024-01-15",
      checkOut: searchData?.checkOut || "2024-01-17",
      rating: "4.2",
      price: "$129/night",
      amenities: ["Free WiFi", "Breakfast", "Parking"]
    },
    {
      id: 4,
      name: "Boutique Resort",
      type: "Boutique Hotel",
      location: searchData?.location || "Downtown",
      checkIn: searchData?.checkIn || "2024-01-15",
      checkOut: searchData?.checkOut || "2024-01-17", 
      rating: "4.6",
      price: "$249/night",
      amenities: ["Free WiFi", "Spa", "Restaurant"]
    }
  ];

  useEffect(() => {
    if (isOpen) {
      setLoading(false);
      setHotels(mockHotels);
    }
  }, [isOpen]);

  const handleSelect = (hotel) => {
    onSelect(hotel);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
      <Card extra="w-[800px] max-h-[90vh] overflow-hidden bg-white/95 dark:bg-navy-800/95 backdrop-blur-sm border border-white/20 dark:border-navy-600/50 shadow-2xl">
        <div className="bg-[#5a88c6] shadow-lg p-6 rounded-t-[20px] relative overflow-hidden sticky top-0 z-20">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="rounded-2xl bg-white/20 backdrop-blur-sm p-3">
                <FaHotel className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">Available Hotels</h3>
                <p className="text-white/80 text-sm">
                  {searchData?.location} • {searchData?.checkIn} to {searchData?.checkOut}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="rounded-full bg-white/20 p-2 text-white hover:bg-white/30 transition-colors">
              <MdClose className="h-6 w-6" />
            </button>
          </div>
        </div>
        
        <div className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-500 border-t-transparent mb-4"></div>
              <p className="text-slate-600 dark:text-slate-400 text-lg font-medium">Searching for hotels...</p>
              <p className="text-slate-500 dark:text-slate-500 text-sm">Please wait while we find the best options</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-slate-700 dark:text-white font-semibold mb-4">
                Found {hotels.length} hotels for your search
              </p>
              {hotels.map((hotel) => (
                <div
                  key={hotel.id}
                  className="group p-6 rounded-2xl bg-gradient-to-r from-white to-slate-50 dark:from-navy-800 dark:to-navy-700 border-2 border-slate-200 dark:border-navy-600 hover:border-purple-400 dark:hover:border-purple-400 hover:shadow-xl transition-all duration-300 cursor-pointer"
                  onClick={() => handleSelect(hotel)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 rounded-2xl bg-[#5a88c6] shadow-lg">
                        <FaHotel className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-slate-800 dark:text-white">{hotel.name}</h4>
                        <div className="flex items-center space-x-2">
                          <p className="text-slate-600 dark:text-slate-400">{hotel.type}</p>
                          <div className="flex items-center space-x-1">
                            <MdStar className="h-4 w-4 text-yellow-500" />
                            <span className="text-slate-600 dark:text-slate-400 text-sm">{hotel.rating}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-600">{hotel.price}</p>
                      <p className="text-slate-500 dark:text-slate-400 text-sm">per night</p>
                    </div>
                  </div>
                  
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <FaHotel className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                      <span className="text-slate-700 dark:text-white font-medium">{hotel.location}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-slate-500 dark:text-slate-400">📅</span>
                      <span className="text-slate-700 dark:text-white font-medium">{hotel.checkIn} - {hotel.checkOut}</span>
                    </div>
                  </div>
                  
                  <div className="mt-3 flex flex-wrap gap-2">
                    {hotel.amenities.map((amenity, index) => (
                      <span key={index} className="px-3 py-1 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded-full text-sm font-medium">
                        {amenity}
                      </span>
                    ))}
                  </div>
                  
                  <div className="mt-4 flex justify-end">
                    <button className="px-6 py-2 bg-[#5a88c6] text-white rounded-xl font-semibold hover:bg-[#4a7bb6] transition-all duration-200 group-hover:scale-105">
                      Select Hotel
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default HotelResultsModal;